/** India BRSR Readiness Checker — paths, scale and methodology version. */

export const BRSR_METHODOLOGY_VERSION = '1.0.0'

export const BRSR_BASE_PATH = '/tools/india-brsr-readiness'

export const BRSR_SESSION_STORAGE_KEY = 'ecopshub.india-brsr.v1'

export const BRSR_SESSION_STORAGE_ENABLED = true

export const BRSR_STEPS = [
  {
    id: 'profile',
    label: 'Profile',
    estimateMin: 2,
    title: 'Company profile',
    intro:
      'These answers tailor the screening. Company name is optional. This tool does not look up stock-exchange rankings.',
  },
  {
    id: 'applicability',
    label: 'Applicability',
    estimateMin: 2,
    title: 'Regulatory applicability',
    intro:
      'The screening applies versioned SEBI rules to your answers. It is a conditional conclusion, not a SEBI determination.',
  },
  {
    id: 'disclosure',
    label: 'Disclosures',
    estimateMin: 3,
    title: 'BRSR disclosure readiness',
    intro:
      'Sections A, B and C follow the BRSR format and the nine NGRBC principles. Essential indicators carry more weight than leadership indicators.',
  },
  {
    id: 'esg_data',
    label: 'ESG data',
    estimateMin: 3,
    title: 'Environmental, social and governance data',
    intro:
      'A spreadsheet is not the same as reliable data. Use the top of the scale only when ownership, method, evidence and review are in place.',
  },
  {
    id: 'evidence',
    label: 'Evidence & Core',
    estimateMin: 2,
    title: 'Evidence, BRSR Core and value chain',
    intro:
      'Evidence maturity is scored separately from disclosure completeness. Core and value-chain questions appear only when your answers make them relevant.',
  },
] as const

export type BrsrStepId = (typeof BRSR_STEPS)[number]['id']

/** Scores are internal diagnostic points, not SEBI marks. Null means exclude from the numerator. */
export const MATURITY_OPTIONS = [
  { value: 'not_in_place', label: 'Not in place / data not collected', score: 0 },
  { value: 'partial', label: 'Partially in place / incomplete data', score: 25 },
  { value: 'weakly_controlled', label: 'Available but manually compiled or weakly controlled', score: 50 },
  { value: 'documented', label: 'Available with documented methodology and supporting evidence', score: 75 },
  { value: 'repeatable', label: 'Repeatable process with validation, ownership and documented review', score: 100 },
  { value: 'not_applicable', label: 'Not applicable', score: null },
  { value: 'not_sure', label: 'Not sure', score: null },
] as const

export type MaturityValue = (typeof MATURITY_OPTIONS)[number]['value']

export const MATURITY_SCORE: Record<MaturityValue, number | null> = {
  not_in_place: 0,
  partial: 25,
  weakly_controlled: 50,
  documented: 75,
  repeatable: 100,
  not_applicable: null,
  not_sure: null,
}

export const SCORE_BANDS = [
  { id: 'significant_gaps', min: 0, max: 39, label: 'Significant readiness gaps' },
  { id: 'developing', min: 40, max: 59, label: 'Developing readiness' },
  { id: 'progressing', min: 60, max: 79, label: 'Progressing toward readiness' },
  { id: 'strong', min: 80, max: 100, label: 'Strong self-reported readiness' },
] as const

export const DIMENSION_LABELS = {
  disclosure: 'Disclosure completeness',
  environmental: 'Environmental and GHG data',
  social_governance: 'Social and governance data',
  evidence: 'Evidence and internal controls',
  brsr_core: 'BRSR Core preparedness',
  value_chain: 'Value-chain readiness',
} as const

export type DimensionId = keyof typeof DIMENSION_LABELS

/** Relative weights when combining dimension scores. Applicability is not a numeric input. */
export const DIMENSION_WEIGHTS: Record<DimensionId, number> = {
  disclosure: 1.2,
  environmental: 1.3,
  social_governance: 1.1,
  evidence: 1.2,
  brsr_core: 1.3,
  value_chain: 1,
}

export function bandForScore(score: number): (typeof SCORE_BANDS)[number] {
  const found = SCORE_BANDS.find((band) => score >= band.min && score <= band.max)
  return found ?? SCORE_BANDS[0]
}

export const MATURITY_SCALE_NOTE =
  'Not applicable and Not sure are not treated as ready. Not applicable is left out of the score and flagged when the indicator may still be required. Not sure is reported as unknown and scores no points.'
